import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { ProjectFilterStatus } from '@/hooks/content-generator/workbook-types';

interface WorkbookContextValue {
  selectedProjectId: string | null;
  filterStatus: ProjectFilterStatus;
  selectedTagId: string | null;
  searchQuery: string;
  isCreateProjectModalOpen: boolean;
  isCreateTagModalOpen: boolean;
  setSelectedProjectId: (id: string | null) => void;
  setFilterStatus: (status: ProjectFilterStatus) => void;
  setSelectedTagId: (id: string | null) => void;
  setSearchQuery: (query: string) => void;
  openCreateProjectModal: () => void;
  closeCreateProjectModal: () => void;
  openCreateTagModal: () => void;
  closeCreateTagModal: () => void;
  // For backward compatibility during transition
  setSelectedPageId: (id: string | null) => void;
  openCreatePageModal: () => void;
  closeCreatePageModal: () => void;
}

const WorkbookContext = createContext<WorkbookContextValue | undefined>(undefined);

export function WorkbookProvider({ children }: { children: React.ReactNode }) {
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<ProjectFilterStatus>('all');
  const [selectedTagId, setSelectedTagId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);
  const [isCreateTagModalOpen, setIsCreateTagModalOpen] = useState(false);

  // Memoize callback functions to prevent unnecessary re-renders
  const openCreateProjectModal = useCallback(() => setIsCreateProjectModalOpen(true), []);
  const closeCreateProjectModal = useCallback(() => setIsCreateProjectModalOpen(false), []);
  const openCreateTagModal = useCallback(() => setIsCreateTagModalOpen(true), []);
  const closeCreateTagModal = useCallback(() => setIsCreateTagModalOpen(false), []);
  
  // Aliases for backward compatibility
  const setSelectedPageId = useCallback((id: string | null) => setSelectedProjectId(id), []);
  const openCreatePageModal = useCallback(() => setIsCreateProjectModalOpen(true), []);
  const closeCreatePageModal = useCallback(() => setIsCreateProjectModalOpen(false), []);
  
  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    selectedProjectId,
    filterStatus,
    selectedTagId,
    searchQuery,
    isCreateProjectModalOpen,
    isCreateTagModalOpen,
    setSelectedProjectId,
    setFilterStatus,
    setSelectedTagId,
    setSearchQuery,
    openCreateProjectModal,
    closeCreateProjectModal,
    openCreateTagModal,
    closeCreateTagModal,
    // For backward compatibility during transition
    setSelectedPageId,
    openCreatePageModal,
    closeCreatePageModal,
  }), [
    selectedProjectId,
    filterStatus,
    selectedTagId,
    searchQuery,
    isCreateProjectModalOpen,
    isCreateTagModalOpen,
    openCreateProjectModal,
    closeCreateProjectModal,
    openCreateTagModal,
    closeCreateTagModal,
    setSelectedPageId,
    openCreatePageModal,
    closeCreatePageModal,
  ]);
  
  return (
    <WorkbookContext.Provider value={value}>
      {children}
    </WorkbookContext.Provider>
  );
}

export function useWorkbook() {
  const context = useContext(WorkbookContext);
  if (context === undefined) {
    throw new Error('useWorkbook must be used within a WorkbookProvider');
  }
  return context;
}
